import {useState} from 'react';
import ProtocolActionsHeader from './ProtocolActionsHeader';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import './ApprovedProtocolActions.scss';
import {ReactComponent as IconLeft} from '../../../../assets/images/icon-arrow-left.svg';
import {useReviewHeaderContext} from '../header/ReviewHeader';
import ProtocolBody from './ProtocolBody';
import ArrModal from '../../../common/arr-modal/ArrModal';
import dayjs from 'dayjs';
import {useDeleteAIProtocolIntelligence} from '../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../shared/constants';
interface ApprovedProtocolActionsProps {
  protocolId: string;
}

const ApprovedProtocolActions = ({
  protocolId,
}: ApprovedProtocolActionsProps) => {
  const {
    fileName,
    lastModifiedAt,
    lastModifiedByUserId,
    setIsEditingProtocol,
    resetStates,
    setReviewHeaderToast,
  } = useReviewHeaderContext();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const {mutateAsync: deleteProtocol, isPending: isDeletePending} =
    useDeleteAIProtocolIntelligence();

  const lastModifiedDate = dayjs
    .utc(lastModifiedAt)
    .format('YYYY/MM/DD HH:mm:ss [UTC]');

  const emailToName = (email: string | null) => {
    return email
      ?.split('@')[0]
      .split('.')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  const handleDeleteProtocol = async () => {
    //delete Protocol api here
    deleteProtocol(
      {protocolId},
      {
        onSuccess: () => {
          resetStates();
          setReviewHeaderToast({
            isOpen: true,
            status: ToastType.Success,
            message: t(
              'ai.review.protocolIntelligence.deleteProtocolModal.deleteProtocolSuccess',
              {
                protocolName: fileName,
              }
            ),
          });
        },
        onError: () => {
          setReviewHeaderToast({
            isOpen: true,
            status: ToastType.Error,
            message: t(
              'ai.review.protocolIntelligence.deleteProtocolModal.deleteProtocolFailed',
              {
                protocolName: fileName,
              }
            ),
          });
        },
      }
    );
  };

  return (
    <Box className="protocol-actions-container">
      <Box className="protocol-actions-header">
        <ArrButton
          variant="secondary"
          size="medium"
          startIcon={<IconLeft />}
          type="button"
          tabIndex={0}
          dataTestId="protocol-back-button"
          onClick={() => setIsEditingProtocol(false)}
        >
          {t('common.back')}
        </ArrButton>
      </Box>
      <ProtocolActionsHeader setIsDeleteModalOpen={setIsDeleteModalOpen} />
      <ProtocolBody />
      <Box className="last-modified-details">
        <Typography
          className="typo-text body3_size text-body-subtle"
          tabIndex={0}
          data-testid="approved-protocol-actions-last-modified"
        >
          {t('ai.review.protocolIntelligence.lastModified', {
            lastModifiedDate: lastModifiedDate,
            lastModifiedBy: emailToName(lastModifiedByUserId),
          })}
        </Typography>
      </Box>
      {isDeleteModalOpen && (
        <ArrModal
          title={t('ai.review.protocolIntelligence.deleteProtocolModal.title', {
            protocolName: fileName,
          })}
          showModal={isDeleteModalOpen}
          onClose={() => {}}
          showClose={false}
          dataTestId="delete-protocol-modal"
          buttons={[
            {
              id: t('common.cancel'),
              label: t('common.cancel'),
              onClick: () => setIsDeleteModalOpen(false),
              variant: 'secondary',
              dataTestId: 'dp-cancel-delete',
            },
            {
              id: t('common.delete'),
              label: t('common.delete'),
              onClick: handleDeleteProtocol,
              variant: 'danger',
              dataTestId: 'dp-confirm-delete',
              loading: isDeletePending,
              disabled: isDeletePending,
            },
          ]}
          className="delete-protocol-modal"
        >
          <Box className="delete-protocol-modal-description">
            <Typography
              className="typo-text body2_Size text-body-strong"
              data-testid="approved-protocol-actions-p1"
            >
              {t('ai.review.protocolIntelligence.deleteProtocolModal.p1')}
            </Typography>
            <Typography
              className="typo-text body2_Size text-body-strong"
              data-testid="approved-protocol-actions-p2"
            >
              {t('ai.review.protocolIntelligence.deleteProtocolModal.p2')}
            </Typography>
          </Box>
        </ArrModal>
      )}
    </Box>
  );
};

export default ApprovedProtocolActions;
